/* eslint-disable @typescript-eslint/no-unused-vars */
import { formatNumberWithSuffix, safeBig } from 'utils/FormatNumber';
import { PoolRowInfo, SmartSwapsDetails } from 'api/types/httpsTypes/contracts';
import { ClockIcon, WalletIcon } from '@heroicons/react/24/outline';
import { EthAddress } from 'modules/ethereum/components/EthAddress';
import Big from 'big.js';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import Tooltip from 'modules/shared-components/tooltip';
import { TokenIconWithChain } from 'modules/shared-components/asset/token-icon';
import { twMerge } from 'tailwind-merge';
import { ChainsIcon } from 'modules/shared-components/exchange/exchange-icon';
import { walletLabels } from '../Wallets/columns';
import { WalletPeriods } from '../Wallets/usePageParams';
import moment from 'moment';
import { Actions } from '../SnipingOpportunitiesCard/columns';
import { TokenStatsWebSocketShortMessage } from 'api/hooks/useTokenStats';
import { PerformanceStyle } from '../../../components/alerts/performanceStyle';
import { SwapRowColumn } from './TableRow';

function MarketCapColumn({ data }: { data: SmartSwapsDetails }) {
  const marketCap = safeBig(data?.tokenUsdMarketcap);

  return marketCap ? (
    <div className="flex space-x-1 items-center">
      <div className="flex space-x-1 items-center">
        <div className=" text-xs ">${formatNumberWithSuffix(marketCap)}</div>
      </div>
    </div>
  ) : (
    <div className="text-xs">--</div>
  );
}

function LiquidityColumn({ data }: { data: SmartSwapsDetails }) {
  const liquidity = safeBig(data?.poolTotalLiquidityInUsd);

  return liquidity ? (
    <div className="flex space-x-1 items-center">
      <div className="flex space-x-1 items-center">
        <div className=" text-xs ">${formatNumberWithSuffix(liquidity)}</div>
      </div>
    </div>
  ) : (
    <div className="text-xs">--</div>
  );
}

export default function PriceColumn({ data }: { data: SmartSwapsDetails }) {
  const price = safeBig(data?.price);

  return price ? (
    <div className="flex space-x-1 items-center">
      <div className=" text-xs ">
        <PerformanceStyle
          pnl={price.toString()}
          isOnlyFlashEnabled={true}
          formatFn={formatNumberWithSuffix}
        />
      </div>
    </div>
  ) : (
    <div className="text-xs">--</div>
  );
}

function MobileColumn({ data }: { data: SmartSwapsDetails }) {
  const price = safeBig(data?.price);

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center xxs:gap-2">
            <TokenIconWithChain
              hasIcon={data.hasIcon}
              tokenName={data.tokenBaseSymbol}
              chain={data.chain ?? Chains.Ethereum}
              protocol={data.dex}
              symbol={data.tokenBaseSymbol}
              className="w-6 h-6"
              address={data.tokenAddress}
            />

            <div className="max-w-[120px] overflow-hidden truncate">
              <div>
                <span className="font-bold text-sm">
                  {data.tokenBaseSymbol}
                </span>
                /
                <span className="xxs:text-dex-white-secondary xxs:text-xs">
                  {data.tokenQuoteSymbol}
                </span>
              </div>

              <div className="flex space-x-1 items-center">
                <EthAddress
                  address={data.tokenAddress}
                  precision={4}
                  kind="token"
                  className="text-xs"
                  chain={data.chain ?? Chains.Ethereum}
                />
                <div className="flex space-x-1 items-center">
                  <div
                    className={twMerge(
                      'text-[10px] rounded text-dex-white xxs:px-1',
                      data.swapDirection === 'buy'
                        ? 'bg-green-500'
                        : 'bg-red-500'
                    )}
                  >
                    {data.swapDirection === 'buy'
                      ? `Buy ${data.blockZeroBlockOne ? '1️⃣ block' : ''}`
                      : 'Sell'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-xs">
          <div className="flex space-x-1 items-center justify-end">
            <div
              className={
                data.swapDirection === 'buy' ? 'text-green-500' : 'text-red-500'
              }
            >
              {formatNumberWithSuffix(Big(data.quoteAmount ?? 0).toNumber())}{' '}
              {data.tokenQuoteSymbol}
            </div>
            <div>
              {price ? (
                <div className="text-dex-white text-xs">
                  <PerformanceStyle
                    pnl={price.toString()}
                    isOnlyFlashEnabled={true}
                    formatFn={formatNumberWithSuffix}
                  />
                </div>
              ) : (
                <div className="text-dex-white text-xs">--</div>
              )}
            </div>
          </div>
          <div className="flex space-x-2 items-center justify-end">
            <div className="flex items-center xxs:space-x-1">
              <WalletIcon className="w-4 h-4" />
              <EthAddress
                className="text-xs"
                address={data.wallet.address}
                precision={3}
                kind="account"
                chain={data.chain}
              />
              {walletLabels(data.wallet, WalletPeriods.Thirty)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const desktopColumns = (
  isTokenPage?: boolean
): SwapRowColumn<SmartSwapsDetails>[] => {
  const columns: SwapRowColumn<SmartSwapsDetails>[] = [
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The smart wallet address, chain, and labels.">
            <div>Wallet</div>
          </Tooltip>
        </div>
      ),
      key: 'ADDRESS',
      component: ({ data }) => (
        <div className="flex items-center xxs:gap-2">
          {!isTokenPage ? (
            <div className="relative">
              <ChainsIcon imgName={data.chain} />
            </div>
          ) : (
            ''
          )}
          <div className="max-w-[120px] overflow-hidden truncate">
            <EthAddress
              className="text-xs"
              address={data.wallet.address}
              precision={3}
              kind="account"
              chain={data.chain}
            />
          </div>
          {walletLabels(data.wallet, WalletPeriods.Thirty)}
        </div>
      ),
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The total amount of tokens purchased or sold.">
            <div>Amount</div>
          </Tooltip>
        </div>
      ),
      key: 'AMOUNT',
      component: ({ data }) => (
        <div className="text-xs">
          <div className="flex space-x-1 items-center">
            <div
              className={
                data.swapDirection === 'buy' ? 'text-green-500' : 'text-red-500'
              }
            >
              {formatNumberWithSuffix(Big(data.quoteAmount ?? 0).toNumber())}{' '}
              {data.tokenQuoteSymbol}
            </div>
          </div>
          <div className="flex space-x-1 items-center">
            <div className="text-[10px]">
              {formatNumberWithSuffix(Big(data.tokenAmount ?? 0).toNumber())}{' '}
            </div>
          </div>
        </div>
      ),
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The price at which the swap has been executed.">
            <div>Price</div>
          </Tooltip>
        </div>
      ),
      key: 'PRICE',
      component: PriceColumn as ({
        data,
      }: {
        data: SmartSwapsDetails;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The fully diluted market cap. at which the swap has been executed.">
            <div>M. Cap</div>
          </Tooltip>
        </div>
      ),
      key: 'MC',
      component: MarketCapColumn as ({
        data,
      }: {
        data: SmartSwapsDetails;
      }) => JSX.Element,
    },
    {
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The total liquidity in USD at which the swap has been executed.">
            <div>Liquidity</div>
          </Tooltip>
        </div>
      ),
      key: 'LIQUIDITY',
      component: LiquidityColumn as ({
        data,
      }: {
        data: SmartSwapsDetails;
      }) => JSX.Element,
    },
  ];

  // Define the "Time" column separately
  const timeColumn: SwapRowColumn<SmartSwapsDetails> = {
    label: (
      <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
        <Tooltip text="The time at which the swap has been executed, and the transaction hash">
          <div>Time</div>
        </Tooltip>
      </div>
    ),
    key: 'TIME',
    component: ({ data }) => (
      <div>
        <div className="text-dex-white">
          {data.timestamp ? (
            <div>
              <div className="flex space-x-1 items-center text-xs">
                <ClockIcon className="h-4 w-4" />
                <div>
                  {moment(data.timestamp).format('DD/MM/YY HH:mm:ss') ?? '--'}
                </div>
              </div>
              <EthAddress
                precision={4}
                className="text-xs"
                address={data.transactionHash}
                chain={data.chain}
                kind="tx"
              />
            </div>
          ) : (
            '--'
          )}
        </div>
      </div>
    ),
  };

  if (isTokenPage) {
    columns.unshift(timeColumn);
  } else {
    columns.unshift({
      label: (
        <div className="flex xxs:space-x-2 items-center hover:text-dex-white xxs:text-dex-white-secondary">
          <Tooltip text="The traded pair">
            <div>Pair</div>
          </Tooltip>
        </div>
      ),
      key: 'PAIR',
      component: ({ data }) => (
        <div>
          <div className="flex items-center xxs:gap-2">
            <TokenIconWithChain
              hasIcon={data.hasIcon}
              tokenName={data.tokenBaseSymbol}
              chain={data.chain ?? Chains.Ethereum}
              protocol={data.dex}
              symbol={data.tokenBaseSymbol}
              className="w-6 h-6"
              address={data.tokenAddress}
            />
            <div className="max-w-[120px] overflow-hidden truncate">
              <div className="flex space-x-1 items-center">
                <div className="font-bold text-sm">{data.tokenBaseSymbol}/</div>
                <div className="xxs:text-dex-white-secondary xxs:text-xs">
                  {data.tokenQuoteSymbol}
                </div>
              </div>
              <div>
                <EthAddress
                  address={data.tokenAddress}
                  precision={4}
                  kind="token"
                  className="text-xs"
                  chain={data.chain ?? Chains.Ethereum}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    });
    columns.push(timeColumn);
    if (!isTokenPage) {
      columns.push({
        label: <div className="xxs:text-dex-white-secondary" />,
        key: 'ACTIONS',
        component: ({ data }) => (
          <Actions
            tokenAddress={data.tokenAddress}
            chain={data.chain}
            symbol={data.tokenBaseSymbol}
            poolAddress={data.tokenAddress}
          />
        ),
      });
    }
  }

  return columns;
};

export const mobileColumns: SwapRowColumn<SmartSwapsDetails>[] = [
  {
    key: 'WALLETS',
    component: MobileColumn as ({
      data,
    }: {
      data: SmartSwapsDetails;
    }) => JSX.Element,
  },
];
